<template>
  <div class="pd-reporte">

    <!-- Encabezado -->
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <h5 class="pd-titulo mb-0">
        <i class="bi bi-shop me-2"></i>Cuentas por Pagar — Compras a Crédito
      </h5>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <span v-if="reporte" class="pd-periodo-badge">{{ reporte.periodo.etiqueta }}</span>
        <template v-if="reporte && !cargando">
          <button
            class="btn btn-sm btn-outline-success"
            @click="exportarXlsx"
            :disabled="exportandoXlsx"
            title="Descargar Excel"
          >
            <span v-if="exportandoXlsx" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-file-earmark-excel me-1"></i>Excel
          </button>
          <button
            class="btn btn-sm btn-outline-danger"
            @click="exportarPdf"
            :disabled="exportandoPdf"
            title="Descargar PDF"
          >
            <span v-if="exportandoPdf" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-file-earmark-pdf me-1"></i>PDF
          </button>
        </template>
      </div>
    </div>

    <!-- Panel filtros -->
    <div class="pd-panel mb-3">
      <div class="pd-panel__cuerpo">

        <!-- Toggle modo -->
        <div class="d-flex gap-2 mb-3">
          <button
            :class="['btn btn-sm', modoFiltro === 'anio' ? 'btn-primary' : 'btn-outline-secondary']"
            @click="modoFiltro = 'anio'"
          ><i class="bi bi-calendar2-range me-1"></i>Por año</button>
          <button
            :class="['btn btn-sm', modoFiltro === 'rango' ? 'btn-primary' : 'btn-outline-secondary']"
            @click="modoFiltro = 'rango'"
          ><i class="bi bi-calendar3 me-1"></i>Rango de fechas</button>
        </div>

        <!-- Selector por año -->
        <div v-if="modoFiltro === 'anio'" class="d-flex flex-wrap gap-2 align-items-center">
          <span class="pd-label-muted me-1">Seleccionar año:</span>
          <button
            v-for="a in aniosDisponibles"
            :key="a"
            :class="['btn btn-sm', anioSeleccionado === a ? 'btn-primary' : 'btn-outline-secondary']"
            @click="seleccionarAnio(a)"
            :disabled="cargando"
          >{{ a }}</button>
        </div>

        <!-- Rango personalizado -->
        <div v-if="modoFiltro === 'rango'" class="row g-2 align-items-end">
          <div class="col-sm-4 col-md-3">
            <label class="form-label form-label-sm mb-1">Fecha desde</label>
            <input type="date" class="form-control form-control-sm pd-input" v-model="filtros.fecha_desde" :max="filtros.fecha_hasta" />
          </div>
          <div class="col-sm-4 col-md-3">
            <label class="form-label form-label-sm mb-1">Fecha hasta</label>
            <input type="date" class="form-control form-control-sm pd-input" v-model="filtros.fecha_hasta" :min="filtros.fecha_desde" />
          </div>
          <div class="col-auto d-flex gap-1">
            <button class="btn btn-sm btn-primary" @click="generarReporte" :disabled="cargando">
              <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-search me-1"></i>{{ cargando ? 'Generando…' : 'Generar' }}
            </button>
            <button class="btn btn-sm btn-link" style="color:var(--text-muted)" @click="limpiarRango" :disabled="cargando">
              Limpiar
            </button>
          </div>
        </div>

      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMensaje" class="pd-alerta pd-alerta--error d-flex align-items-center gap-2 mb-3">
      <i class="bi bi-x-circle-fill"></i>
      <span>{{ errorMensaje }}</span>
    </div>

    <!-- Cargando -->
    <div v-if="cargando" class="pd-estado-vacio">
      <span class="spinner-border spinner-border-sm me-2"></span>Generando reporte…
    </div>

    <template v-if="reporte && !cargando">

      <!-- KPI cards -->
      <div class="row g-3 mb-3">
        <div class="col-sm-6 col-xl">
          <div class="pd-kpi">
            <div class="pd-kpi__valor" style="color:var(--primary-color)">{{ reporte.resumen.proveedores_distintos }}</div>
            <div class="pd-kpi__etiqueta">Proveedores distintos</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl">
          <div class="pd-kpi">
            <div class="pd-kpi__valor" style="color:var(--primary-color)">{{ reporte.resumen.num_compras }}</div>
            <div class="pd-kpi__etiqueta">Compras a crédito</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl">
          <div class="pd-kpi">
            <div class="pd-kpi__valor" style="color:var(--accent-success)">{{ formatearMoneda(reporte.resumen.total_credito) }}</div>
            <div class="pd-kpi__etiqueta">Total emitido</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl">
          <div class="pd-kpi">
            <div class="pd-kpi__valor" style="color:var(--accent-warning)">{{ formatearMoneda(reporte.resumen.total_saldo) }}</div>
            <div class="pd-kpi__etiqueta">Saldo pendiente</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl">
          <div class="pd-kpi">
            <div
              class="pd-kpi__valor"
              :style="reporte.resumen.total_vencido > 0 ? 'color:var(--accent-danger)' : 'color:var(--text-muted)'"
            >{{ formatearMoneda(reporte.resumen.total_vencido) }}</div>
            <div class="pd-kpi__etiqueta">Total vencido</div>
          </div>
        </div>
      </div>

      <!-- Resumen por año -->
      <div v-if="reporte.resumen_anual.length > 0" class="pd-panel mb-3">
        <div class="pd-panel__cabecera">
          <i class="bi bi-bar-chart me-2"></i>Resumen por año
        </div>
        <div class="table-responsive">
          <table class="pd-tabla">
            <thead>
              <tr>
                <th>Año</th>
                <th class="text-end">Compras</th>
                <th class="text-end">Proveedores</th>
                <th class="text-end">Total crédito</th>
                <th class="text-end">Saldo pendiente</th>
                <th class="text-end">Total vencido</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="fila in reporte.resumen_anual"
                :key="fila.anio"
                class="pd-fila"
                :class="{ 'pd-fila--activa': esFilaActiva(fila.anio) }"
              >
                <td>
                  <span class="fw-semibold">{{ fila.anio }}</span>
                  <span v-if="esFilaActiva(fila.anio)" class="pd-badge-sel ms-1">seleccionado</span>
                </td>
                <td class="text-end">{{ fila.num_compras }}</td>
                <td class="text-end">{{ fila.proveedores_distintos }}</td>
                <td class="text-end fw-semibold" style="color:var(--accent-success)">{{ formatearMoneda(fila.total_credito) }}</td>
                <td class="text-end fw-semibold" style="color:var(--accent-warning)">{{ formatearMoneda(fila.total_saldo) }}</td>
                <td class="text-end">
                  <span v-if="fila.total_vencido > 0" class="pd-badge-vencido">{{ formatearMoneda(fila.total_vencido) }}</span>
                  <span v-else class="pd-muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Tabla de compras -->
      <div class="pd-panel mb-3">
        <div class="pd-panel__cabecera d-flex align-items-center justify-content-between">
          <span><i class="bi bi-bag me-2"></i>Compras a crédito — {{ reporte.periodo.etiqueta }}</span>
          <span class="pd-conteo">{{ reporte.compras.length }} compra(s)</span>
        </div>
        <div v-if="reporte.compras.length > 0" class="table-responsive">
          <table class="pd-tabla">
            <thead>
              <tr>
                <th style="width:38px">#</th>
                <th>Fecha emis.</th>
                <th>N.° Fact. proveedor</th>
                <th>Proveedor</th>
                <th>RUC / Ident.</th>
                <th class="text-center">Plazo</th>
                <th class="text-center">Vence</th>
                <th class="text-end">Total</th>
                <th class="text-end">Abonado</th>
                <th class="text-end">Saldo pendiente</th>
                <th class="text-center">Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(c, idx) in reporte.compras"
                :key="c.id"
                class="pd-fila"
                :class="{ 'pd-fila--vencida': c.vencida }"
              >
                <td class="text-center pd-muted">{{ idx + 1 }}</td>
                <td>{{ c.fecha_emision }}</td>
                <td class="fw-semibold" style="font-family:monospace">{{ c.numero_factura || '—' }}</td>
                <td>{{ c.proveedor }}</td>
                <td class="pd-muted">{{ c.identificacion }}</td>
                <td class="text-center pd-muted">{{ c.dias_plazo_credito }}d</td>
                <td class="text-center">{{ c.fecha_vencimiento_credito || '—' }}</td>
                <td class="text-end" style="color:var(--text-secondary)">{{ formatearMoneda(c.total) }}</td>
                <td class="text-end" style="color:var(--accent-warning)">
                  {{ c.monto_abonado > 0 ? formatearMoneda(c.monto_abonado) : '—' }}
                </td>
                <td
                  class="text-end fw-semibold"
                  :style="c.vencida ? 'color:var(--accent-danger)' : 'color:var(--accent-success)'"
                >
                  {{ formatearMoneda(c.saldo_pendiente) }}
                </td>
                <td class="text-center">
                  <span v-if="c.vencida" class="pd-badge pd-badge--vencida">
                    <i class="bi bi-exclamation-triangle-fill me-1"></i>Vencida ({{ c.dias_vencido }}d)
                  </span>
                  <span v-else class="pd-badge pd-badge--vigente">
                    <i class="bi bi-check-circle-fill me-1"></i>Vigente
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="pd-pie">
                <td colspan="7">Total</td>
                <td class="text-end" style="color:var(--text-secondary)">{{ formatearMoneda(reporte.resumen.total_credito) }}</td>
                <td></td>
                <td class="text-end" style="color:var(--accent-warning)">{{ formatearMoneda(reporte.resumen.total_saldo) }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else class="pd-estado-vacio">
          <i class="bi bi-shop fs-3 d-block mb-2"></i>
          No hay compras a crédito con saldo pendiente en este período.
          <div class="mt-2" style="font-size:.85rem">
            Para que aparezcan aquí, activa la opción <strong>"¿Es a crédito?"</strong> al registrar una compra.
          </div>
        </div>
      </div>

    </template>

    <!-- Estado inicial -->
    <div v-if="!reporte && !cargando && !errorMensaje" class="pd-panel">
      <div class="pd-estado-vacio">
        <i class="bi bi-shop fs-2 d-block mb-2"></i>
        Selecciona un año o rango de fechas para ver las compras a crédito pendientes.
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'
import themeManager from '@/store/theme'
import * as XLSX from 'xlsx'
import { fechaIsoSistema } from '@/utils/fechaSistema'

export default {
  name: 'ClientReportesProveedoresDeuda',

  data() {
    const hoy = new Date()
    return {
      modoFiltro: 'anio',
      anioSeleccionado: hoy.getFullYear(),
      filtros: {
        fecha_desde: `${hoy.getFullYear()}-01-01`,
        fecha_hasta: fechaIsoSistema(hoy),
      },
      cargando: false,
      errorMensaje: null,
      reporte: null,
      exportandoXlsx: false,
      exportandoPdf: false,
    }
  },

  mounted() {
    this.generarReporte()
  },

  computed: {
    themeClass() {
      return themeManager.themeClass
    },

    aniosDisponibles() {
      const actual = new Date().getFullYear()
      const set = new Set([actual])
      if (this.reporte?.resumen_anual) {
        this.reporte.resumen_anual.forEach(r => set.add(Number(r.anio)))
      }
      return Array.from(set).sort((a, b) => b - a)
    },
  },

  methods: {
    async generarReporte() {
      this.cargando = true
      this.errorMensaje = null
      this.reporte = null
      try {
        const params = new URLSearchParams()
        if (this.modoFiltro === 'anio') {
          params.set('anio', this.anioSeleccionado)
        } else {
          if (this.filtros.fecha_desde) params.set('fecha_desde', this.filtros.fecha_desde)
          if (this.filtros.fecha_hasta) params.set('fecha_hasta', this.filtros.fecha_hasta)
        }
        const { data } = await api.get(`/reportes/proveedores-deuda?${params}`)
        if (data.success) {
          this.reporte = data
        } else {
          this.errorMensaje = data.error || 'No se pudieron obtener los datos.'
        }
      } catch (err) {
        this.errorMensaje = err?.response?.data?.error || 'Error al conectar con el servidor.'
      } finally {
        this.cargando = false
      }
    },

    seleccionarAnio(anio) {
      this.anioSeleccionado = anio
      this.generarReporte()
    },

    limpiarRango() {
      const hoy = new Date()
      this.filtros.fecha_desde = `${hoy.getFullYear()}-01-01`
      this.filtros.fecha_hasta = fechaIsoSistema(hoy)
      this.reporte = null
      this.errorMensaje = null
    },

    esFilaActiva(anio) {
      return this.modoFiltro === 'anio' && Number(anio) === this.anioSeleccionado
    },

    formatearMoneda(valor) {
      return '$' + parseFloat(valor || 0).toFixed(2)
    },

    // ── Exportar Excel ────────────────────────────────────────────────
    exportarXlsx() {
      if (!this.reporte) return
      this.exportandoXlsx = true
      try {
        const r  = this.reporte
        const wb = XLSX.utils.book_new()

        // Hoja Resumen
        const resumen = [
          ['Reporte de Cuentas por Pagar — Compras a Crédito'],
          ['Período', r.periodo.etiqueta],
          [],
          ['Concepto', 'Valor'],
          ['Proveedores distintos',   r.resumen.proveedores_distintos],
          ['Compras a crédito',       r.resumen.num_compras],
          ['Total emitido a crédito', r.resumen.total_credito],
          ['Saldo pendiente',         r.resumen.total_saldo],
          ['Total vencido',           r.resumen.total_vencido],
        ]
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resumen), 'Resumen')

        // Hoja por año
        if (r.resumen_anual.length > 0) {
          const cabA = ['Año', 'Compras', 'Proveedores', 'Total crédito', 'Saldo pendiente', 'Total vencido']
          const filA = r.resumen_anual.map(f => [
            f.anio, f.num_compras, f.proveedores_distintos,
            parseFloat(f.total_credito), parseFloat(f.total_saldo), parseFloat(f.total_vencido),
          ])
          XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([cabA, ...filA]), 'Por año')
        }

        // Hoja compras
        const cabC = [
          'Fecha emisión', 'N.° Fact. proveedor', 'Proveedor', 'RUC / Identificación',
          'Días plazo', 'Fecha vencimiento', 'Total', 'Abonado', 'Saldo pendiente', 'Estado',
        ]
        const filC = r.compras.map(c => [
          c.fecha_emision,
          c.numero_factura || '',
          c.proveedor,
          c.identificacion,
          c.dias_plazo_credito,
          c.fecha_vencimiento_credito || '',
          parseFloat(c.total),
          parseFloat(c.monto_abonado),
          parseFloat(c.saldo_pendiente),
          c.vencida ? `Vencida (${c.dias_vencido}d)` : 'Vigente',
        ])
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([cabC, ...filC]), 'Compras')

        const etiq = r.periodo.etiqueta.replace(/[^a-z0-9]/gi, '_')
        XLSX.writeFile(wb, `proveedores_deuda_${etiq}.xlsx`)
      } finally {
        this.exportandoXlsx = false
      }
    },

    // ── Exportar PDF ──────────────────────────────────────────────────
    async exportarPdf() {
      if (!this.reporte) return
      this.exportandoPdf = true
      try {
        const params = new URLSearchParams()
        if (this.modoFiltro === 'anio') {
          params.set('anio', this.anioSeleccionado)
        } else {
          if (this.filtros.fecha_desde) params.set('fecha_desde', this.filtros.fecha_desde)
          if (this.filtros.fecha_hasta) params.set('fecha_hasta', this.filtros.fecha_hasta)
        }
        const response = await api.get(
          `/reportes/proveedores-deuda/pdf?${params}`,
          { responseType: 'blob' }
        )
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url  = URL.createObjectURL(blob)
        const a   = document.createElement('a')
        const etiq = this.reporte.periodo.etiqueta.replace(/[^a-z0-9]/gi, '_')
        a.href     = url
        a.download = `proveedores_deuda_${etiq}.pdf`
        a.click()
        URL.revokeObjectURL(url)
      } catch {
        this.errorMensaje = 'No se pudo generar el PDF. Intente nuevamente.'
      } finally {
        this.exportandoPdf = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Contenedor ── */
.pd-reporte {
  padding: 1.25rem;
}

.pd-titulo {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--primary-color);
}

.pd-periodo-badge {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--secondary-bg-soft);
  border: 1px solid var(--border-main);
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
}

/* ── Panel ── */
.pd-panel {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.pd-panel__cuerpo {
  padding: 0.85rem 1rem;
}

.pd-panel__cabecera {
  padding: 0.6rem 0.85rem;
  background: var(--secondary-bg-soft);
  border-bottom: 1px solid var(--border-main);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
}

/* ── KPI ── */
.pd-kpi {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  text-align: center;
  padding: 1rem;
}

.pd-kpi__valor {
  font-size: 1.5rem;
  font-weight: 700;
}

.pd-kpi__etiqueta {
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
}

/* ── Alertas ── */
.pd-alerta {
  padding: 0.5rem 0.85rem;
  border-radius: 8px;
  font-size: 0.875rem;
  border: 1px solid;
}

.pd-alerta--error {
  background: var(--accent-danger-light);
  border-color: var(--accent-danger);
  color: var(--accent-danger);
}

/* ── Estado vacío ── */
.pd-estado-vacio {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
  font-size: 0.9rem;
}

/* ── Tabla ── */
.pd-tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.pd-tabla thead tr {
  background: var(--secondary-bg-soft);
  border-bottom: 2px solid var(--border-main);
}

.pd-tabla thead th {
  padding: 0.6rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.pd-fila {
  border-bottom: 1px solid var(--border-main);
  transition: background 0.1s;
}

.pd-fila td {
  padding: 0.55rem 0.75rem;
  vertical-align: middle;
  color: var(--text-primary);
}

.pd-fila:hover {
  background: var(--table-hover);
}

.pd-fila--activa {
  background: var(--accent-info-light) !important;
  border-left: 3px solid var(--primary-color);
}

.pd-fila--vencida {
  background: var(--accent-danger-light, rgba(220,53,69,0.06)) !important;
}

/* ── Pie tabla ── */
.pd-pie td {
  padding: 0.55rem 0.75rem;
  font-weight: 600;
  color: var(--text-primary);
  background: var(--secondary-bg-soft);
  border-top: 2px solid var(--border-main);
}

/* ── Badges ── */
.pd-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  white-space: nowrap;
}

.pd-badge--vencida {
  background: var(--accent-danger-light);
  color: var(--accent-danger);
}

.pd-badge--vigente {
  background: var(--accent-success-light);
  color: var(--accent-success);
}

.pd-badge-vencido {
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  background: var(--accent-danger-light);
  color: var(--accent-danger);
}

.pd-badge-sel {
  font-size: 0.7rem;
  background: var(--accent-info-light);
  color: var(--primary-color);
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  font-weight: 500;
}

.pd-muted {
  color: var(--text-muted);
  font-size: 0.85rem;
}

.pd-conteo {
  font-size: 0.8rem;
  color: var(--text-muted);
}

.pd-label-muted {
  font-size: 0.85rem;
  color: var(--text-muted);
}

.pd-input {
  background: var(--secondary-bg-main);
  border-color: var(--border-main);
  color: var(--text-primary);
}
</style>
